import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import { Logo, Icon } from '@scentregroup/ui'

import { Link as LinkType } from '../../../types/link'

import Search, { AlgoliaApiKeys } from '../search'
import SearchButton from '../search-button'

import style from './index.scss'
import Nav from './nav/main'
import Account from './nav/account'
import SearchBar from './search-bar'
import W from './w.inline.svg'
import { WithNavigationMenu, Centre, Cart, LinkItem, User } from './types'
import { LinkContext } from './context'

export { default as useCart } from './use-cart'

const MAX_PINNED_LINKS = 5

const UniversalHeader: React.FC<{
  Link: LinkType
  country: string
  // Used within the Find A Centre selector
  centres?: Array<Centre>
  // When specified, will show the centre's `navigationMenu` items in the Nav
  centre?: Centre
  giftCards?: WithNavigationMenu
  title?: LinkItem
  cart?: Cart
  // A set of links to show immediately below the header
  pinnedLinks?: { items: Array<LinkItem>; hideMore?: boolean }
  account: {
    baseUrl: string
    from?: string
    redirect?: string
  }
  // The currently logged in user
  user?: User
  // Navigation structure to show in the "Shop" section
  nationalNav?: WithNavigationMenu
  algoliaApiKeys?: AlgoliaApiKeys
}> = ({
  Link,
  country,
  centres,
  centre,
  giftCards,
  title,
  cart,
  pinnedLinks,
  account,
  user,
  nationalNav,
  algoliaApiKeys,
}) => {
  const universalHeaderRef = useRef<HTMLDivElement>(null)

  const [isNav, setIsNav] = useState(false)
  const [isAccount, setIsAccount] = useState(false)

  useEffect(() => {
    const el = universalHeaderRef.current || document.createElement('div')

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(
      ([e]) => el.classList.toggle(style.stuck, e.intersectionRatio < 1),
      { threshold: [1] }
    )

    observer.observe(el)

    return () => observer.disconnect()
  })

  return (
    <>
      <div
        ref={universalHeaderRef}
        className={classNames(style['universal-header'])}
      >
        <div className={classNames(style['header-container'])}>
          <div className={classNames(style['header-contents'])}>
            <button
              className={classNames(style.link, style.desktop)}
              onClick={() => setIsNav(true)}
            >
              <Icon
                size="medium"
                type="hamburger"
                className={classNames(style.icon)}
              />
            </button>
            {title ? (
              <>
                <Link to="/" className={classNames(style.link, style.desktop)}>
                  <W />
                </Link>
                <Link
                  to="/"
                  className={classNames(
                    style.link,
                    style.mobile,
                    style.unstuck
                  )}
                >
                  <W />
                </Link>
                <Link to={title.url} className={classNames(style.link)}>
                  {title.label}
                </Link>
              </>
            ) : (
              <Link to="/" className={classNames(style.link)}>
                <Logo className={classNames(style.desktop)} />
                <Logo className={classNames(style.mobile, style.unstuck)} />
                <W className={classNames(style.mobile, style.stuck)} />
              </Link>
            )}
            <div className={classNames(style.icons)}>
              {algoliaApiKeys && (
                <>
                  <div className={classNames(style.desktop)}>
                    <Search
                      isEvolved
                      algoliaApiKeys={algoliaApiKeys}
                      centre={centre}
                      country={country}
                    >
                      {showSearch => (
                        <SearchBar
                          showSearch={showSearch}
                          className={classNames(style.search)}
                        />
                      )}
                    </Search>
                  </div>
                  <Search
                    isEvolved
                    algoliaApiKeys={algoliaApiKeys}
                    centre={centre}
                    country={country}
                  >
                    {showSearch => (
                      <SearchButton
                        showSearch={showSearch}
                        className={classNames(
                          style.icon,
                          style.mobile,
                          style.stuck
                        )}
                      >
                        <Icon size="medium" type="search" />
                      </SearchButton>
                    )}
                  </Search>
                </>
              )}
              <button
                className={classNames(
                  user?.firstName && style.avatar,
                  style.link
                )}
                onClick={() => setIsAccount(true)}
              >
                {user?.firstName ? (
                  user.firstName.charAt(0).toUpperCase()
                ) : (
                  <Icon
                    size="medium"
                    type="user"
                    className={classNames(style.icon)}
                  />
                )}
              </button>
              {cart && (
                <Link
                  title="Cart"
                  to={cart.url}
                  className={classNames(style.link, style.icon, {
                    [style['cart-active']]: Boolean(cart.count),
                  })}
                >
                  <Icon
                    size="medium"
                    type="shopping-cart"
                    className={classNames(style.icon)}
                  />
                </Link>
              )}
              <button
                className={classNames(style.link, style.mobile)}
                onClick={() => setIsNav(true)}
              >
                <Icon
                  size="medium"
                  type="hamburger"
                  className={classNames(style.icon)}
                />
              </button>
            </div>
          </div>
        </div>
        {pinnedLinks && (
          <div className={classNames(style['links-container'], style.desktop)}>
            <div className={classNames(style['links-contents'])}>
              {pinnedLinks.items.slice(0, MAX_PINNED_LINKS).map((item, i) => (
                <Link key={i} to={item.url} className={classNames(style.link)}>
                  {item.label}
                </Link>
              ))}
              {!pinnedLinks.hideMore && (
                <button
                  className={classNames(style.link)}
                  onClick={() => setIsNav(true)}
                >
                  More...
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={classNames(style.search, style.mobile)}>
        {algoliaApiKeys && (
          <Search
            isEvolved
            algoliaApiKeys={algoliaApiKeys}
            centre={centre}
            country={country}
          >
            {showSearch => <SearchBar showSearch={showSearch} />}
          </Search>
        )}
      </div>
      {isNav && (
        <LinkContext.Provider
          value={({ onClick, ...props }) => (
            <Link
              onClick={e => {
                onClick?.(e)
                setIsNav(false)
              }}
              {...props}
            />
          )}
        >
          <Nav
            country={country}
            centres={centres}
            centre={centre}
            shop={nationalNav}
            giftCards={giftCards}
            onClose={() => setIsNav(false)}
          />
        </LinkContext.Provider>
      )}
      {isAccount && (
        <LinkContext.Provider
          value={({ onClick, ...props }) => (
            <Link
              onClick={e => {
                onClick?.(e)
                setIsAccount(false)
              }}
              {...props}
            />
          )}
        >
          <Account
            {...account}
            user={user}
            onClose={() => setIsAccount(false)}
          />
        </LinkContext.Provider>
      )}
    </>
  )
}

export default UniversalHeader
